import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Stack,
  Divider,
  useTheme,
  Button,
  Tooltip,
  Fade,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  EditNote as EditNoteIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  LocalOffer as LocalOfferIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { Avatar } from "@mui/material";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import {
  fetchMilestones,
  fetchMilestoneDetails,
} from "store/features/company/projectManagement/milestone/milestoneActions";
import {
  selectMilestones,
  selectMilestonesLoading,
  selectSelectedMilestone,
  selectMilestoneDetailsLoading,
} from "store/features/company/projectManagement/milestone/milestoneSelectors";
import AddMilestoneModal from "./AddMilestoneModal";
import EditMilestoneModal from "./EditMilestoneModal";
import usePermissions from "hooks/usePermissions";

const ProjectDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const [isAddMilestoneOpen, setIsAddMilestoneOpen] = useState(false);
  const [isEditMilestoneOpen, setIsEditMilestoneOpen] = useState(false);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
  const tasksScrollRef = React.useRef(null);

  const projects = useSelector(selectProjects);
  const departments = useSelector(selectDepartments);
  const employees = useSelector(selectEmployees);
  const milestones = useSelector(selectMilestones);
  const milestonesLoading = useSelector(selectMilestonesLoading);
  const selectedMilestone = useSelector(selectSelectedMilestone);
  const milestoneDetailsLoading = useSelector(selectMilestoneDetailsLoading);

  const projectFromState = location.state?.project;
  const project =
    projectFromState || projects.find((p) => String(p.id) === String(id));

  useEffect(() => {
    if (project?.id) {
      dispatch(fetchMilestones(project.id));
    }
  }, [project?.id, dispatch]);

  const projectMilestones = milestones.filter(
    (m) => String(m.project_id) === String(project?.id)
  );

  // Default to the first milestone once the list loads
  useEffect(() => {
    if (projectMilestones.length > 0 && !selectedMilestoneId) {
      setSelectedMilestoneId(projectMilestones[0].id);
    }
  }, [projectMilestones.length, projectMilestones]); // eslint-disable-line react-hooks/exhaustive-deps

  // FIXED: Use useEffect instead of useMemo for API calls
  useEffect(() => {
    if (selectedMilestoneId) {
      dispatch(fetchMilestoneDetails(selectedMilestoneId));
    }
  }, [selectedMilestoneId, dispatch]);

  const handleSelectMilestone = (milestoneId) => {
    setSelectedMilestoneId(milestoneId);
  };

  const scrollTasks = (direction) => {
    const el = tasksScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 320, behavior: "smooth" });
  };

  const departmentMap = {};
  departments.forEach((d) => (departmentMap[d.id] = d.name));

  const employeeMap = {};
  employees.forEach((e) => (employeeMap[e.id] = `${e.first_name} ${e.last_name}`));

  const isDark = theme.palette.mode === "dark";

  const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return `${day}/${month}/${String(year).slice(-2)}`;
  };

  const formatBudget = (budget) => {
    if (!budget) return "₹0";
    const num = parseFloat(budget);
    if (isNaN(num)) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatDateISO = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getTaskPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "#dc2626";
      case "high":
        return "#f97316";
      case "medium":
        return "#eab308";
      case "low":
        return "#22c55e";
      default:
        return "#9ca3af";
    }
  };

  const avatarPalette = ["#3b82f6", "#8b5cf6", "#ec4899", "#f97316", "#14b8a6", "#6366f1"];

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .trim()
      .split(/\s+/)
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (!project) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <FolderIcon sx={{ fontSize: 56, color: "#d1d5db", mb: 2 }} />
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, mb: 1 }}>
          Project not found
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>
          We couldn't locate this project. It may have been removed, or you
          navigated here directly without selecting one from the list.
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/projects")}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "#fff",
            px: 3,
            "&:hover": {
              background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
            },
          }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  const priorityColor =
    project.priority === "urgent"
      ? isDark
        ? "#f472b6"
        : "#db2777"
      : project.priority === "high"
        ? isDark
          ? "#fb3c3c"
          : "#ea0c0c"
        : project.priority === "medium"
          ? isDark
            ? "#fbbf24"
            : "#d97706"
          : project.priority === "low"
            ? isDark
              ? "#4ade80"
              : "#16a34a"
            : isDark
              ? "#9ca3af"
              : "#6b7280";

  const statusStyles = {
    planning: { bg: "#fef3c7", color: "#92400e", icon: <EditNoteIcon sx={{ fontSize: 16 }} /> },
    active: { bg: "#d1fae5", color: "#065f46", icon: <RadioButtonCheckedIcon sx={{ fontSize: 16 }} /> },
    on_hold: { bg: "#fef3c7", color: "#92400e", icon: <RadioButtonCheckedIcon sx={{ fontSize: 16 }} /> },
    completed: { bg: "#dbeafe", color: "#1e40af", icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    cancelled: { bg: "#fee2e2", color: "#991b1b", icon: <CancelIcon sx={{ fontSize: 16 }} /> },
  };
  const statusStyle = statusStyles[project.status] || {
    bg: "#e5e7eb",
    color: "#374151",
    icon: undefined,
  };
  const statusLabel =
    project.status === "on_hold"
      ? "On Hold"
      : project.status
        ? project.status.charAt(0).toUpperCase() + project.status.slice(1)
        : "N/A";

  const cardBg = isDark ? "#111827" : "#ffffff";
  const cardBorder = isDark ? "#1e293b" : "#f3f4f6";
  const labelColor = isDark ? "#9ca3af" : "#6b7280";
  const textColor = isDark ? "#ffffff" : "#111827";
  const subTextColor = isDark ? "#d1d5db" : "#4b5563";

  const sectionLabelSx = {
    fontWeight: 700,
    fontSize: "0.7rem",
    color: labelColor,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    mb: 1,
  };

  const teamMembers = (project.team_members || []).filter(
    (memberId) => memberId !== project.project_manager_id
  );

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      sx={{ p: { xs: 2, md: 3 }, width: "100%" }}
    >
      {/* Back navigation */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: labelColor,
          mb: 2,
          "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f3f4f6" },
        }}
      >
        Back to Projects
      </Button>

      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: cardBg,
          border: `1px solid ${cardBorder}`,
          borderLeft: `5px solid ${priorityColor}`,
          boxShadow: isDark
            ? "0 4px 16px -4px rgba(0,0,0,0.35)"
            : "0 4px 16px -4px rgba(0,0,0,0.06)",
          p: 3,
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ width: "100%" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  backgroundColor: isDark ? "#2a2d3c" : "#f3f4f6",
                  borderRadius: "6px",
                  padding: "3px 10px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    color: priorityColor,
                    display: "inline-block",
                  }}
                >
                  #{project.id}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", color: textColor, mb: 0.5 }}>
                {project.name}
              </Typography>
            </Stack>
            {project.description && (
              <Typography sx={{ fontSize: "0.9rem", fontStyle: "italic", color: labelColor, maxWidth: 650 }}>
                {project.description}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center"
            sx={{ ml: "auto", flexShrink: 0, display: "flex" }}>
            <Chip
              icon={
                project.priority === "urgent" ? <WarningIcon sx={{ fontSize: 14 }} /> :
                  project.priority === "high" ? <TrendingUpIcon sx={{ fontSize: 14 }} /> :
                    project.priority === "medium" ? <TrendingFlatIcon sx={{ fontSize: 14 }} /> :
                      project.priority === "low" ? <TrendingDownIcon sx={{ fontSize: 14 }} /> : undefined
              }
              label={project.priority?.toUpperCase() || "N/A"}
              variant="outlined"
              sx={{
                borderColor: priorityColor,
                color: priorityColor,
                fontWeight: "bold",
                fontStyle: "italic",
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
            <Chip
              icon={statusStyle.icon}
              label={statusLabel}
              sx={{
                backgroundColor: statusStyle.bg,
                color: statusStyle.color,
                fontWeight: 600,
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Info grid */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          mb: 3,
        }}
      >
        {/* Timeline & Budget */}
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 260px",
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            p: 2.5,
          }}
        >
          <Typography sx={sectionLabelSx}>Timeline</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <EventIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography sx={{ fontFamily: "monospace", fontSize: "0.9rem", color: subTextColor }}>
              {formatDateShort(project.start_date)} — {formatDateShort(project.end_date)}
            </Typography>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={sectionLabelSx}>Budget</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MoneyIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: isDark ? "#fbbf24" : "#d97706" }}>
              {formatBudget(project.budget)}
            </Typography>
          </Stack>
        </Paper>

        {/* Department & Lead */}
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 260px",
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            p: 2.5,
          }}
        >
          <Typography sx={sectionLabelSx}>Department</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <FolderIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: subTextColor }}>
              {departmentMap[project.department_id] || `Department ${project.department_id}`}
            </Typography>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={sectionLabelSx}>Project Lead</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: subTextColor }}>
              {employeeMap[project.project_manager_id] || `Employee ${project.project_manager_id}`}
            </Typography>
          </Stack>
        </Paper>

        {/* Tags */}
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 260px",
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            p: 2.5,
          }}
        >
          <Typography sx={sectionLabelSx}>Tags</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {project.tags && project.tags.length > 0 ? (
              project.tags.map((tag, idx) => (
                <Chip
                  key={idx}
                  label={tag}
                  size="small"
                  icon={<LocalOfferIcon />}
                  sx={{
                    backgroundColor: "transparent",
                    color: isDark ? "#4bb0de" : "#0284c7",
                    fontStyle: "italic",
                    "& .MuiChip-icon": { color: isDark ? "#4bb0de" : "#0284c7" },
                  }}
                />
              ))
            ) : (
              <Typography sx={{ fontSize: "0.85rem", color: labelColor }}>No tags</Typography>
            )}
          </Stack>
        </Paper>
      </Box>

      {/* Team members */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          backgroundColor: cardBg,
          border: `1px solid ${cardBorder}`,
          p: 2.5,
        }}
      >
        <Typography sx={sectionLabelSx}>Team</Typography>
        <Stack spacing={1.5}>
          {project.project_manager_id && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 1.5,
                borderRadius: "12px",
                backgroundColor: isDark ? "rgba(245,158,11,0.15)" : "#fffbeb",
                border: `1px solid #f59e0b`,
              }}
            >
              <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "primary.main" }}>
                  {employeeMap[project.project_manager_id] || `Employee ${project.project_manager_id}`}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: labelColor }}>
                  ID: {project.project_manager_id} • Project Lead
                </Typography>
              </Box>
            </Box>
          )}

          {teamMembers.length > 0 ? (
            teamMembers.map((memberId, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "#f9fafb",
                }}
              >
                <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
                <Box>
                  <Typography sx={{ fontWeight: 500, fontSize: "0.9rem", color: textColor }}>
                    {employeeMap[memberId] || `Employee ${memberId}`}
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: labelColor }}>ID: {memberId}</Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: "0.85rem", color: labelColor, textAlign: "center", py: 1 }}>
              No additional team members assigned
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Milestones */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          backgroundColor: cardBg,
          border: `1px solid ${cardBorder}`,
          p: 2.5,
          mt: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={sectionLabelSx}>Milestones</Typography>
          {hasPermission('projects.update') && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setIsAddMilestoneOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: "8px",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(245,158,11,0.1)" : "#fffbeb",
                },
              }}
            >
              Add Milestone
            </Button>
          )}
        </Box>

        {milestonesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} sx={{ color: "primary.main" }} />
          </Box>
        ) : projectMilestones.length > 0 ? (
          (() => {
            // FIXED: Correctly extract data from the API response structure
            const activeSummary = projectMilestones.find(
              (m) => String(m.id) === String(selectedMilestoneId)
            );

            // Check if we have details and extract the nested data
            const hasDetails = selectedMilestone &&
              String(selectedMilestone.milestone?.id) === String(selectedMilestoneId);

            // Extract milestone data based on the API structure
            const milestone = hasDetails
              ? selectedMilestone.milestone
              : activeSummary;

            const statistics = hasDetails
              ? selectedMilestone.statistics
              : null;

            const milestoneTasks = hasDetails
              ? selectedMilestone.tasks || []
              : [];

            const totalTasks = statistics?.total_tasks || 0;
            const pendingTasks = statistics?.todo_tasks || 0;

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 3,
                }}
              >
                {/* Milestone tabs */}
                <Stack
                  spacing={1.25}
                  sx={{
                    width: { xs: "100%", md: 220 },
                    flexShrink: 0,
                  }}
                >
                  {projectMilestones.map((ms) => {
                    const isActive =
                      String(ms.id) === String(selectedMilestoneId);
                    return (
                      <Button
                        key={ms.id}
                        onClick={() => handleSelectMilestone(ms.id)}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          borderRadius: "10px",
                          py: 1.1,
                          px: 2,
                          ...(isActive
                            ? {
                              background:
                                "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                              color: "#fff",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                              },
                            }
                            : {
                              border: `1px solid ${isDark ? "#475569" : "#f59e0b"}`,
                              color: "primary.main",
                              "&:hover": {
                                backgroundColor: isDark
                                  ? "rgba(245,158,11,0.1)"
                                  : "#fffbeb",
                              },
                            }),
                        }}
                      >
                        {ms.title}
                      </Button>
                    );
                  })}
                </Stack>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", md: "block" } }}
                />

                {/* Selected milestone details */}
                {milestoneDetailsLoading && !hasDetails ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      py: 4,
                    }}
                  >
                    <CircularProgress size={24} sx={{ color: "primary.main" }} />
                  </Box>
                ) : milestone ? (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1.5}
                    >
                      <Typography
                        sx={{ fontWeight: 800, fontSize: "1.15rem", color: textColor }}
                      >
                        {milestone.title}
                        <Box component="span" sx={{ mx: 1, color: labelColor }}>

                        </Box>
                        <Chip
                          label={milestone.status}
                          size="small"
                          sx={{
                            paddingX: 1,
                            height: 24,
                            fontSize: "0.65rem",
                            fontWeight: 600,
                          }}
                        />
                      </Typography>
                      {hasPermission('projects.update') && (
                        <Button
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setIsEditMilestoneOpen(true)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            borderRadius: "8px",
                            color: "primary.main",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: isDark
                                ? "rgba(245,158,11,0.1)"
                                : "#fffbeb",
                            },
                          }}
                        >
                          Edit Milestone
                        </Button>
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 0.5, mb: 1.5 }}
                    >
                      <Typography
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: labelColor }}
                      >
                        {formatDateISO(milestone.start_date)}
                      </Typography>
                      <Typography sx={{ color: labelColor, fontSize: "0.8rem" }}>→</Typography>
                      <Typography
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: labelColor }}
                      >
                        {formatDateISO(milestone.due_date)}
                      </Typography>
                    </Stack>

                    {milestone.description ? (
                      <Typography sx={{ fontSize: "0.85rem", color: subTextColor, mb: 2 }}>
                        {milestone.description}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: "0.85rem", color: labelColor, mb: 2, fontStyle: "italic" }}>
                        No description added
                      </Typography>
                    )}

                    {hasDetails && (
                      <Stack direction="row" spacing={4} sx={{ mb: 2.5 }}>
                        <Box>
                          <Typography sx={sectionLabelSx}>Total Tasks</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", color: textColor }}>
                            {totalTasks}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={sectionLabelSx}>Pending Tasks</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", color: textColor }}>
                            {pendingTasks}
                          </Typography>
                        </Box>
                      </Stack>
                    )}

                    <Typography sx={sectionLabelSx}>Tasks</Typography>

                    {milestoneTasks.length > 0 ? (
                      <Box sx={{ position: "relative" }}>
                        <Box
                          ref={tasksScrollRef}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            overflowX: "auto",
                            pb: 1,
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                          }}
                        >
                          {milestoneTasks.map((task) => {
                            // FIXED: Use assigned_employee directly from API
                            const assigneeName = task.assigned_employee || "Unassigned";
                            const avatarColor =
                              avatarPalette[
                              Number(task.id || 0) % avatarPalette.length
                              ];
                            const pColor = getTaskPriorityColor(task.priority);

                            return (
                              <Paper
                                key={task.id}
                                elevation={0}
                                sx={{
                                  flex: "0 0 240px",
                                  borderRadius: "12px",
                                  p: 2,
                                  backgroundColor: isDark
                                    ? "rgba(30,41,59,0.5)"
                                    : "#f9fafb",
                                  borderLeft: `4px solid ${pColor}`,
                                }}
                              >
                                <Box>

                                </Box>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.85rem",
                                    color: textColor,
                                    mb: 0.5,
                                  }}
                                >
                                  {task.title}
                                </Typography>
                                {task.description && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: subTextColor,
                                      mb: 1,
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {task.description}
                                  </Typography>
                                )}
                                <Typography
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.65rem",
                                    color: labelColor,
                                    mb: 1.5,
                                  }}
                                >
                                  {formatDateShort(task.start_date)} —{" "}
                                  {formatDateShort(task.due_date)}
                                </Typography>
                                <Stack
                                  direction="row"
                                >
                                  {assigneeName !== "Unassigned" ? (
                                    <Tooltip
                                      title={assigneeName}
                                      arrow
                                      TransitionComponent={Fade}
                                      TransitionProps={{ timeout: 300 }}
                                      placement="top"
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "#1e293b",
                                            color: "#fff",
                                            fontSize: "0.8rem",
                                            fontWeight: 500,
                                            px: 1.5,
                                            py: 0.8,
                                            borderRadius: "8px",
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                                          },
                                        },
                                        arrow: {
                                          sx: {
                                            color: "#1e293b",
                                          },
                                        },
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          marginRight: 1,
                                          borderRadius: '20px',
                                          width: 34,
                                          height: 24,
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          bgcolor: avatarColor,
                                          cursor: "pointer",
                                          transition: "all 0.25s ease",
                                          "&:hover": {
                                            transform: "scaleX(1.12)",
                                            boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                                          },
                                        }}
                                      >
                                        {getInitials(assigneeName)}
                                      </Avatar>
                                    </Tooltip>
                                  ) : (
                                    <Box />
                                  )}
                                  {task.priority && (
                                    <Chip
                                      label={task.priority}
                                      size="small"
                                      sx={{
                                        height: 24,
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                        color: pColor,
                                        backgroundColor: isDark
                                          ? "rgba(255,255,255,0.06)"
                                          : "#ffffff",
                                        border: `1px solid ${pColor}`,
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Box>
                        {milestoneTasks.length > 3 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            sx={{ mt: 1 }}
                          >
                            <IconButton size="small" onClick={() => scrollTasks(-1)}>
                              <ChevronLeftIcon sx={{ fontSize: 18, color: labelColor }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => scrollTasks(1)}>
                              <ChevronRightIcon sx={{ fontSize: 18, color: labelColor }} />
                            </IconButton>
                          </Stack>
                        )}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: labelColor,
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        {hasDetails ? "No tasks in this milestone yet" : ""}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>
            );
          })()
        ) : (
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: labelColor,
              textAlign: "center",
              py: 2,
            }}
          >
            No milestones yet
          </Typography>
        )}
      </Paper>

      <AddMilestoneModal
        isOpen={isAddMilestoneOpen}
        onClose={() => setIsAddMilestoneOpen(false)}
        onSuccess={() => dispatch(fetchMilestones(project.id))}
        projectId={project.id}
        project={project}
      />
      <EditMilestoneModal
        isOpen={isEditMilestoneOpen}
        onClose={() => setIsEditMilestoneOpen(false)}
        onSuccess={() => {
          dispatch(fetchMilestones(project.id));
          if (selectedMilestoneId) {
            dispatch(fetchMilestoneDetails(selectedMilestoneId));
          }
        }}
        milestone={
          String(selectedMilestone?.milestone?.id) === String(selectedMilestoneId)
            ? selectedMilestone.milestone
            : projectMilestones.find((m) => String(m.id) === String(selectedMilestoneId))
        }
        project={project}
      />
    </Box>
  );
};

export default ProjectDetails;
